import React from "react"
import { twMerge } from "tailwind-merge"

import { Button } from "../components/shared/Buttons"
import Typography from "../components/shared/Typography"
import { CalendarFlyoutProvider } from "../contexts/CalendarFlyoutContext"
import { ToastProvider } from "../contexts/ToastContext"
import GraphQLProvider from "../providers/GraphQLProvider"

import BookableEventCard from "./BookableEvents/BookableEventCard"
import { BookableEventsProvider, useBookableEvents } from "./BookableEvents/BookableEventsContext"
import BookableEventsFlyout from "./BookableEvents/BookableEventsFlyout"

const BookableEventsPage = () => {
  const { setShowFlyout, bookableEvents, setBookableEvent, defaultNewEvent, defaultTimeZone, theme } =
    useBookableEvents()

  // Helper to get button border radius based on theme
  const getButtonBorderRadius = () => {
    if (!theme) return undefined
    switch (theme.buttonStyle) {
      case "square":
        return "0px"
      case "rounded":
        return "12px"
      case "pill":
        return "50px"
      default:
        return "12px"
    }
  }

  // Helper to get button classes based on theme
  const getButtonClasses = () => {
    if (!theme) return ""
    switch (theme.buttonStyle) {
      case "square":
        return "!rounded-none"
      case "rounded":
        return "!rounded-xl"
      case "pill":
        return "!rounded-full"
      default:
        return "!rounded-xl"
    }
  }

  // Generate button styles for both v1 and v2 themes
  const getButtonStyle = () => {
    if (!theme) return undefined
    return {
      backgroundColor: `${theme.colors?.primary} !important`,
      borderColor: `${theme.colors?.primary} !important`,
      color: `${theme.colors?.textLight} !important`,
      fontFamily: theme.fonts?.paragraph,
      borderRadius: theme.borderRadius || getButtonBorderRadius()
    }
  }

  const handleButtonHover = (e, isHovering) => {
    if (!theme) return
    e.target.style.backgroundColor = isHovering ? theme.colors?.primaryDark : theme.colors?.primary
    e.target.style.borderColor = isHovering ? theme.colors?.primaryDark : theme.colors?.primary
  }

  return (
    <>
      <div className="mb-8 flex justify-between align-bottom sm:mb-4">
        <div className="flex flex-col">
          <Typography as="h1" variant="h3">
            Events
          </Typography>
          <Typography variant="subtitle">Schedule events that multiple people can sign up for</Typography>
        </div>
        <div className="flex">
          <Button
            type={theme ? "skip-default-classnames" : "primary"}
            size="small"
            className={twMerge(
              theme
                ? "h-8 border px-4 font-bold text-white transition-all hover:shadow focus:outline-none focus:ring-2 focus:ring-blue"
                : "",
              getButtonClasses()
            )}
            style={getButtonStyle()}
            floatingInMobile={true}
            onClick={() => {
              setShowFlyout(true)
              setBookableEvent({ ...defaultNewEvent, timeZone: defaultTimeZone })
            }}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}>
            Create event
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4">
        {bookableEvents.map((event) => (
          <BookableEventCard
            event={event}
            key={event.id}
            onClick={() => {
              setShowFlyout(true)
              setBookableEvent(event)
            }}
          />
        ))}
      </div>
      <BookableEventsFlyout />
    </>
  )
}

// TODO: CalendarFlyoutProvider should not be here, but BookableEventForm is using it now
export default function BookableEvents(props) {
  return (
    <GraphQLProvider>
      <ToastProvider>
        <CalendarFlyoutProvider>
          <BookableEventsProvider {...props}>
            <BookableEventsPage />
          </BookableEventsProvider>
        </CalendarFlyoutProvider>
      </ToastProvider>
    </GraphQLProvider>
  )
}
